import { useState, useRef, useEffect } from "react";
import { Mic, MicOff, Play, Pause, Square, Check, Trash2 } from "lucide-react";

interface NoteAudioRecorderProps {
  onAudioReady: (audioBlob: Blob) => void;
  onCancel: () => void;
}

export function NoteAudioRecorder({
  onAudioReady,
  onCancel,
}: NoteAudioRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm;codecs=opus" });
        setRecordedAudio(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      startTimeRef.current = Date.now();

      intervalRef.current = setInterval(() => {
        setDuration((Date.now() - startTimeRef.current) / 1000);
      }, 100);
    } catch (error) {
      console.error("Erro ao acessar microfone:", error);
      alert("Erro ao acessar o microfone. Verifique as permissões.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setDuration((Date.now() - startTimeRef.current) / 1000);
    }
  };

  const playAudio = () => {
    audioRef.current?.play();
    setIsPlaying(true);
  };

  const pauseAudio = () => {
    audioRef.current?.pause();
    setIsPlaying(false);
  };

  const resetRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRecordedAudio(null);
    setAudioUrl("");
    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);
    setIsRecording(false);
  };

  const confirmAudio = () => {
    if (recordedAudio) onAudioReady(recordedAudio);
  };

  const handleCancel = () => {
    resetRecording();
    onCancel();
  };

  const formatTime = (time: number) => {
    if (!time || isNaN(time) || !isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="bg-cinza-100 border border-cinza-200 rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-600">
          {isRecording ? "Gravando..." : recordedAudio ? "Áudio gravado" : "Gravar áudio"}
        </span>
        <button
          type="button"
          onClick={handleCancel}
          className="text-gray-400 hover:text-red-500 transition-colors"
          title="Cancelar"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="flex items-center gap-2">
        {!recordedAudio ? (
          <button
            type="button"
            onClick={isRecording ? stopRecording : startRecording}
            className={`p-2 rounded-full transition-colors flex-shrink-0 ${
              isRecording
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-azul-400 text-white hover:bg-azul-500"
            }`}
            title={isRecording ? "Parar gravação" : "Iniciar gravação"}
          >
            {isRecording ? <Square size={16} /> : <Mic size={16} />}
          </button>
        ) : (
          <button
            type="button"
            onClick={isPlaying ? pauseAudio : playAudio}
            className="p-2 rounded-full bg-azul-400 text-white hover:bg-azul-500 transition-colors flex-shrink-0"
            title={isPlaying ? "Pausar" : "Reproduzir"}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>
        )}

        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-600">
            {formatTime(isRecording ? duration : currentTime)}
            {recordedAudio && !isRecording && ` / ${formatTime(duration)}`}
          </div>
          {recordedAudio && (
            <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
              <div
                className="bg-azul-400 h-1 rounded-full transition-all duration-100"
                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
              />
            </div>
          )}
        </div>

        {recordedAudio && (
          <div className="flex gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={resetRecording}
              className="p-1.5 rounded-full bg-gray-400 text-white hover:bg-gray-500 transition-colors"
              title="Gravar novamente"
            >
              <MicOff size={14} />
            </button>
            <button
              type="button"
              onClick={confirmAudio}
              className="p-1.5 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors"
              title="Confirmar áudio"
            >
              <Check size={14} />
            </button>
          </div>
        )}
      </div>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
          onEnded={() => {
            setIsPlaying(false);
            setCurrentTime(0);
          }}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (d && !isNaN(d) && isFinite(d)) setDuration(d);
          }}
        />
      )}

      {isRecording && (
        <div className="flex items-center justify-center gap-1.5 mt-2 text-red-500 text-xs">
          <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
          REC
        </div>
      )}
    </div>
  );
}
